import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { MIN_PASSWORD } from '@aop/domain';
import { ApiError, api, type ActivationInfo } from '../lib/api';
import { Card, Field } from './AuthScreens';

type View = { k: 'loading' } | { k: 'error'; msg: string } | { k: 'info'; info: ActivationInfo };

/**
 * Trang mở từ link trong email: /activate/<mã>. Người dùng tự đặt mật khẩu (không có mật khẩu tạm nào đi qua email).
 * Link hết hạn / đã bị thay bằng link mới → có nút "Gửi link mới" gửi lại tới đúng email đã đăng ký.
 */
export function ActivateScreen({ token }: { token: string }) {
  const [view, setView] = useState<View>({ k: 'loading' });
  const [pw, setPw] = useState(''), [pw2, setPw2] = useState('');
  const [err, setErr] = useState(''), [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ username: string; status: string } | null>(null);
  const [sent, setSent] = useState<{ to: string; dryRun: boolean } | null>(null);

  const load = useCallback(async () => {
    setView({ k: 'loading' });
    try { setView({ k: 'info', info: await api.activation.info(token) }); }
    catch (e) { setView({ k: 'error', msg: e instanceof ApiError ? e.message : 'Không tải được thông tin liên kết.' }); }
  }, [token]);
  useEffect(() => { void load(); }, [load]);

  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr('');
    if (pw.length < MIN_PASSWORD) return setErr(`Mật khẩu tối thiểu ${MIN_PASSWORD} ký tự.`);
    if (pw !== pw2) return setErr('Hai lần nhập mật khẩu không khớp.');
    setBusy(true);
    try { setDone(await api.activation.setPassword(token, pw)); }
    catch (e2) {
      if (e2 instanceof ApiError && e2.status === 409) await load();            // link vừa hết hạn / vừa được dùng ở nơi khác → hiện đúng trạng thái
      else setErr(e2 instanceof ApiError ? e2.message : 'Không lưu được mật khẩu, thử lại.');
    }
    setBusy(false);
  };

  const resend = async () => {
    setErr(''); setBusy(true);
    try { setSent(await api.activation.resend(token)); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Không gửi được link mới, thử lại.'); }
    setBusy(false);
  };

  const home = () => { window.location.assign('/'); };

  if (view.k === 'loading') return <Card loading="Đang kiểm tra liên kết…" />;
  if (view.k === 'error') {
    return (
      <Card>
        <h2>Không mở được liên kết</h2>
        <p className="auth-err" role="alert">{view.msg}</p>
        <button type="button" className="btn primary" onClick={() => void load()}>Thử lại</button>
      </Card>
    );
  }

  if (done) {
    return (
      <Card>
        <h2>Đã đặt mật khẩu</h2>
        <p>Mật khẩu của tài khoản <b>{done.username}</b> đã được lưu.</p>
        {done.status === 'pending'
          ? <p className="hint">Tài khoản sẽ đăng nhập được sau khi quản trị kích hoạt. Anh/chị sẽ nhận thông báo khi tài khoản được kích hoạt.</p>
          : <p className="hint">Anh/chị có thể đăng nhập bằng mật khẩu mới.</p>}
        <button type="button" className="btn primary" onClick={home}>Về trang đăng nhập</button>
      </Card>
    );
  }

  const { info } = view;
  if (info.state === 'ok') {
    const reset = info.kind === 'reset';
    return (
      <Card>
        <form onSubmit={submit}>
          <h2>{reset ? 'Đặt lại mật khẩu' : 'Đặt mật khẩu để bắt đầu'}</h2>
          <p className="hint">Xin chào <b>{info.fullName || info.username}</b>. Tên đăng nhập của anh/chị: <b>{info.username}</b>.</p>
          <Field id="acPw" label={`Mật khẩu (tối thiểu ${MIN_PASSWORD} ký tự)`} type="password" value={pw} onChange={e => setPw(e.target.value)} autoComplete="new-password" autoFocus />
          <Field id="acPw2" label="Nhập lại mật khẩu" type="password" value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" />
          <p className="auth-err" role="alert">{err}</p>
          <button type="submit" className="btn primary" disabled={busy}>{busy ? 'Đang lưu…' : 'Lưu mật khẩu'}</button>
        </form>
      </Card>
    );
  }

  if (info.state === 'used') {
    return (
      <Card>
        <h2>Liên kết đã được dùng</h2>
        <p className="hint">Mật khẩu đã được đặt bằng liên kết này. Hãy đăng nhập; nếu quên mật khẩu, nhờ quản trị gửi link mới.</p>
        <button type="button" className="btn primary" onClick={home}>Về trang đăng nhập</button>
      </Card>
    );
  }

  if (info.state === 'expired' || info.state === 'replaced') {
    const replaced = info.state === 'replaced';
    return (
      <Card>
        <h2>{replaced ? 'Liên kết đã được thay bằng liên kết mới' : 'Liên kết đã hết hạn'}</h2>
        {sent ? (
          <>
            <p role="status">{sent.dryRun ? 'Hệ thống đang ở chế độ chạy thử nên chưa gửi thư thật. Hãy nhờ quản trị gửi link.' : <>Đã gửi link mới tới <b>{sent.to}</b>. Hãy mở thư (kể cả mục thư rác) và bấm vào link mới.</>}</p>
            <p className="hint">Link cũ không còn dùng được.</p>
          </>
        ) : (
          <>
            <p className="hint">{replaced ? 'Anh/chị đã có một link mới hơn trong hộp thư: hãy dùng link mới nhất, hoặc bấm nút dưới đây để gửi lại.' : 'Link chỉ có hiệu lực trong một thời gian ngắn. Bấm nút dưới đây để nhận link mới qua email đã đăng ký.'}</p>
            <p className="auth-err" role="alert">{err}</p>
            <button type="button" className="btn primary" disabled={busy} onClick={() => void resend()}>{busy ? 'Đang gửi…' : 'Gửi link mới'}</button>
          </>
        )}
      </Card>
    );
  }

  return (
    <Card>
      <h2>Liên kết không hợp lệ</h2>
      <p className="hint">Liên kết này không đúng hoặc tài khoản không còn tồn tại. Hãy liên hệ quản trị để được gửi link mới.</p>
      <button type="button" className="btn primary" onClick={home}>Về trang đăng nhập</button>
    </Card>
  );
}
